import store from '@/store';
import blobStorageService from '@/features/Admin/services/blobstorage.service.js';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

class DownloadManagerService {
  constructor() {
    this.componentInstance = null;
    this.lastReportedSizes = new Map(); // Track last reported size for each download
  }

  static getInstance() {
    if (!DownloadManagerService.instance) {
      DownloadManagerService.instance = new DownloadManagerService();
    }
    return DownloadManagerService.instance;
  }

  setComponentInstance(component) {
    this.componentInstance = component;
  }

  // Public API methods
  addDownload(url, filename = null) {
    return store.dispatch('DOWNLOAD_MANAGER/ADD_DOWNLOAD', { url, filename });
  }

  pauseDownload(downloadId) {
    store.dispatch('DOWNLOAD_MANAGER/PAUSE_DOWNLOAD', downloadId);
  }

  resumeDownload(downloadId) {
    store.dispatch('DOWNLOAD_MANAGER/RESUME_DOWNLOAD', downloadId);
  }

  cancelDownload(downloadId) {
    store.dispatch('DOWNLOAD_MANAGER/CANCEL_DOWNLOAD', downloadId);
    // Clean up tracking data for cancelled download
    this.lastReportedSizes.delete(downloadId);
  }

  retryDownload(downloadId) {
    store.dispatch('DOWNLOAD_MANAGER/RETRY_DOWNLOAD', downloadId);
  }

  removeDownload(downloadId) {
    store.dispatch('DOWNLOAD_MANAGER/REMOVE_DOWNLOAD', downloadId);
    // Clean up tracking data
    this.lastReportedSizes.delete(downloadId);
  }

  clearCompleted() {
    store.dispatch('DOWNLOAD_MANAGER/CLEAR_COMPLETED');
  }

  clearAll() {
    store.dispatch('DOWNLOAD_MANAGER/CLEAR_ALL');
    // Clear all tracking data
    this.lastReportedSizes.clear();
  }

  show() {
    store.dispatch('DOWNLOAD_MANAGER/SHOW');
  }

  hide() {
    store.dispatch('DOWNLOAD_MANAGER/HIDE');
  }

  toggleMinimize() {
    store.dispatch('DOWNLOAD_MANAGER/TOGGLE_MINIMIZE');
  }

  // Getters
  get isVisible() {
    return store.getters['DOWNLOAD_MANAGER/GET_IS_VISIBLE'];
  }

  get isMinimized() {
    return store.getters['DOWNLOAD_MANAGER/GET_IS_MINIMIZED'];
  }

  get downloads() {
    return store.getters['DOWNLOAD_MANAGER/GET_DOWNLOADS'];
  }

  get activeDownloads() {
    return store.getters['DOWNLOAD_MANAGER/GET_ACTIVE_DOWNLOADS'];
  }

  get completedDownloads() {
    return store.getters['DOWNLOAD_MANAGER/GET_COMPLETED_DOWNLOADS'];
  }

  get hasCompletedDownloads() {
    return store.getters['DOWNLOAD_MANAGER/HAS_COMPLETED_DOWNLOADS'];
  }

  getDownloadById(id) {
    return store.getters['DOWNLOAD_MANAGER/GET_DOWNLOAD_BY_ID'](id);
  }

  // Core download functionality
  async startDownload(download) {
    try {
      // Check if the File System Access API is supported (for modern browsers)
      if ('showSaveFilePicker' in window) {
        await this.downloadWithProgress(download);
      } else {
        // Fallback to traditional download
        await this.downloadWithFallback(download);
      }
    } catch (error) {
      debugError('downloadManagerService', 'Download failed:', error);
      this.updateDownloadStatus(download.id, 'error', 0);
      store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD', {
        id: download.id,
        updates: { errorMessage: error.message || 'Download failed' }
      });
    }
  }

  async downloadWithProgress(download) {
    try {
      // Get the AbortController for this download
      const controller = store.getters['DOWNLOAD_MANAGER/GET_CONTROLLER_BY_ID'](download.id);
      if (!controller) {
        throw new Error('Download controller not found');
      }

      // Prepare headers for range request if resuming
      const headers = { 'Accept-Ranges': 'bytes' };
      const resumeFromByte = download.downloadedSize || 0;

      if (resumeFromByte > 0) {
        headers['Range'] = `bytes=${resumeFromByte}-`;
      }

      // Start the fetch request with chunked download support and abort signal
      const response = await fetch(download.url, {
        headers,
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the total size for progress calculation
      const contentLength = response.headers.get('content-length');
      const contentRange = response.headers.get('content-range');

      let totalSize = 0;
      let downloadedSize = resumeFromByte;

      if (contentRange) {
        // Parse Content-Range header: "bytes 200-1023/1024"
        const match = contentRange.match(/bytes (\d+)-(\d+)\/(\d+)/);
        if (match) {
          totalSize = parseInt(match[3], 10);
        }
      } else if (contentLength) {
        totalSize = resumeFromByte + parseInt(contentLength, 10);
      }

      const chunks = [];
      const chunkSize = 64 * 1024; // 64KB chunks for optimal performance
      let lastProgressReport = 0; // Track last progress report time

      // Try to use File System Access API for better UX
      let fileHandle;

      // Check if this is a resume operation
      if (resumeFromByte > 0) {
        // For resume operations, use range-aware fallback method
        await this.downloadWithRangeSupport(download);
        return;
      } else {
        // New download - prompt for file location
        try {
          const extension = download.filename.split('.').pop() || '';
          fileHandle = await window.showSaveFilePicker({
            suggestedName: download.filename,
            types: [{
              description: `${extension.toUpperCase()} files`,
              accept: { [`application/${extension}`]: [`.${extension}`] }
            }]
          });

        } catch (err) {
          if (err.name === 'AbortError') {
            // User cancelled
            this.updateDownloadStatus(download.id, 'cancelled', 0);
            return;
          }
          // Fallback to regular download
          await this.downloadWithFallback(download);
          return;
        }
      }

      // Create writable stream for efficient chunked writing
      // For resume, we need to position at the correct byte offset
      const writable = await fileHandle.createWritable({
        keepExistingData: resumeFromByte > 0
      });

      if (resumeFromByte > 0) {
        // Seek to the resume position
        await writable.seek(resumeFromByte);
      }

      // Use modern for-await approach for streaming chunks
      const reader = response.body.getReader();

      try {
        let isDone = false;
        while (!isDone) {
          // Check if download is paused
          const currentDownload = store.getters['DOWNLOAD_MANAGER/GET_DOWNLOAD_BY_ID'](download.id);
          if (currentDownload?.isPaused) {
            // Save current progress and pause
            await writable.close();
            store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD_STATUS', {
              id: download.id,
              status: 'paused',
              progress: currentDownload.progress
            });
            return;
          }

          // Check if download was cancelled
          if (controller.signal.aborted) {
            await writable.close();
            store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD_STATUS', {
              id: download.id,
              status: 'cancelled',
              progress: 0
            });
            return;
          }

          const { done, value } = await reader.read();

          if (done) {
            isDone = true;
            break;
          }

          // Process chunk and write to file
          await writable.write(value);
          downloadedSize += value.length;
          chunks.push(value);

          const currentTime = Date.now();

          // Report progress to blob storage API every 10 seconds
          if (currentTime - lastProgressReport >= 10000) {
            await this.reportProgressToBlobStorage(download.id, downloadedSize, totalSize, chunks.length, value.length);
            lastProgressReport = currentTime;
          }

          // Calculate and update progress with better granularity
          const progress = totalSize > 0 ? Math.round((downloadedSize / totalSize) * 100) :
                         Math.min(99, Math.round((downloadedSize / (downloadedSize + chunkSize)) * 100));

          // Update progress in store (keep UI updates frequent for smooth progress bar)
          store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD_PROGRESS', {
            id: download.id,
            progress,
            downloadedSize,
            totalSize
          });

          // Yield control to prevent UI blocking during large downloads
          if (chunks.length % 50 === 0) {
            await new Promise(resolve => setTimeout(resolve, 1));
          }
        }
      } finally {
        reader.releaseLock();
      }

      await writable.close();

      // Final progress report
      await this.reportProgressToBlobStorage(download.id, downloadedSize, totalSize, chunks.length, 0, true);
      store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD_STATUS', {
        id: download.id,
        status: 'completed',
        progress: 100
      });

    } catch (error) {
      await this.reportProgressToBlobStorage(download.id, 0, 0, 0, 0, false);
      store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD_STATUS', {
        id: download.id,
        status: 'error',
        progress: 0
      });
      throw error;
    }
  }

  async downloadWithRangeSupport(download) {
    try {
      const resumeFromByte = download.downloadedSize || 0;
      const headers = { 'Accept-Ranges': 'bytes' };

      if (resumeFromByte > 0) {
        headers['Range'] = `bytes=${resumeFromByte}-`;
      }

      // Get the controller for this download
      const controller = store.getters['DOWNLOAD_MANAGER/GET_CONTROLLER_BY_ID'](download.id);
      if (!controller) {
        throw new Error('Download controller not found');
      }

      // Fetch with range headers for resume support
      const response = await fetch(download.url, {
        headers,
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create blob and download link for range-aware fallback
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = download.filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      window.URL.revokeObjectURL(url);

      // Mark as completed
      this.updateDownloadStatus(download.id, 'completed', 100);

    } catch (error) {
      debugError('downloadManagerService', 'Range-aware download failed:', error);
      this.updateDownloadStatus(download.id, 'error', 0);
      store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD', {
        id: download.id,
        updates: { errorMessage: error.message || 'Download failed' }
      });
    }
  }

  async downloadWithFallback(download) {
    // Traditional download method
    const link = document.createElement('a');
    link.href = download.url;
    link.download = download.filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Simulate progress for traditional downloads
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += Math.random() * 15;
      if (progress >= 95) {
        clearInterval(progressInterval);
        progress = 100;
        this.updateDownloadStatus(download.id, 'completed', 100);
      } else {
        this.updateDownloadStatus(download.id, 'downloading', Math.round(progress));
      }
    }, 200);
  }

  updateDownloadStatus(downloadId, status, progress = undefined) {
    store.dispatch('DOWNLOAD_MANAGER/UPDATE_DOWNLOAD_STATUS', {
      id: downloadId,
      status,
      progress
    });
  }

  async reportProgressToBlobStorage(downloadId, downloadedSize, totalSize, chunkCount, currentChunkSize = 0, isCompleted = false) {
    try {
      // Get the current download for filename
      const download = store.getters['DOWNLOAD_MANAGER/GET_DOWNLOAD_BY_ID'](downloadId);
      if (!download) return;

      const facilityId = this.getCurrentFacilityId();
      if (!facilityId) {
        debugWarn('downloadManagerService', 'No facility ID available for progress reporting');
        return;
      }

      // Calculate delta size (difference from last reported size)
      const lastReportedSize = this.lastReportedSizes.get(downloadId) || 0;
      const deltaSize = downloadedSize - lastReportedSize;

      // Update the last reported size
      this.lastReportedSizes.set(downloadId, downloadedSize);

      let deltaSizeInMb = deltaSize / (1024 * 1024);
      const progressData = {
        FacilityId: facilityId,
        FileName: download.filename,
        FileSizeBytes: totalSize || null,
        BytesDownloaded: deltaSizeInMb, // Report delta size instead of total
        CurrentChunkSize: currentChunkSize,
        ChunkCount: chunkCount,
        IsCompleted: isCompleted
      };

      // Use blob storage service to report progress
      await blobStorageService.reportProgress(progressData);

    } catch (error) {
      debugWarn('downloadManagerService', 'Error reporting progress to blob storage:', error);
      // Continue download even if progress reporting fails
    }
  }

  getCurrentFacilityId() {
    // Since we don't have access to $route in the service, we'll need to get it from the router
    // This can be improved by passing the facility ID when initializing downloads
    try {
      const router = require('@/router').default;
      return router.currentRoute?.params?.id;
    } catch (error) {
      debugWarn('downloadManagerService', 'Could not access router in service:', error);
      return null;
    }
  }
}

// Create singleton instance
const downloadManagerService = DownloadManagerService.getInstance();

export default downloadManagerService;